/**
 * useContextPeek — espiada de contexto da conversa ancorada
 * (Mensageria Condensada · superfície 3 · PR F1b).
 *
 * Consome `GET /v1/conversations/:id/context-peek` (PR B3). Resumo Vivo + mídias +
 * notas + sensibilidade da conversa do cliente vinculada à mensagem interna.
 *
 * GATEADO no `enabled` (não só no render — "gatear o fetch"):
 *   - cap `direct-message-participate` (espelha o gate server-side); E
 *   - `conversationId` não-nulo (sem âncora, sem fetch).
 *
 * O 403 é estado de PRODUTO honesto ("sem permissão"), não erro genérico: o
 * api-client usa `suppressForbiddenToast` (sem toast), e este hook deriva o flag
 * `denied` do `ApiError` 403, expondo também o corpo mínimo (`sensitivityLevel` +
 * `reason`) para a UI etiquetar a sensibilidade sem revelar conteúdo.
 */

import { useQuery } from '@tanstack/react-query';

import { useCapability } from '../../../../hooks/use-capability';
import { ApiError, api } from '../../../../lib/api-client';

import type { ContextPeekDenied, ContextPeekResponse } from '../../types';

export const CONTEXT_PEEK_KEY = 'direct-messages-context-peek';

export const contextPeekKey = (
  conversationId: string,
  viaThreadId: string | null,
): readonly [string, string, string] => [
  CONTEXT_PEEK_KEY,
  conversationId,
  viaThreadId ?? '',
];

interface UseContextPeekOptions {
  /** Thread interno de origem (opcional; ajuda a auditoria server-side). */
  viaThreadId?: string | null;
  /** Override de gating do consumidor (default `true`). */
  enabled?: boolean;
}

interface UseContextPeekResult {
  data: ContextPeekResponse | undefined;
  /** `true` quando o backend negou (403) — render do estado honesto, não erro. */
  denied: boolean;
  /** Corpo mínimo do 403 (sensibilidade + motivo) quando `denied`. */
  deniedDetail: ContextPeekDenied | null;
  isLoading: boolean;
  /** Erro NÃO-403 (rede, 404, 5xx) — distinto de `denied`. */
  isError: boolean;
  refetch: () => void;
}

function isForbidden(err: unknown): err is ApiError {
  return err instanceof ApiError && err.status === 403;
}

export function useContextPeek(
  conversationId: string | null,
  options: UseContextPeekOptions = {},
): UseContextPeekResult {
  const { viaThreadId = null, enabled = true } = options;
  const canRead = useCapability('direct-message-participate');

  const query = useQuery({
    queryKey: contextPeekKey(conversationId ?? '', viaThreadId),
    queryFn: () => api.getContextPeek(conversationId!, viaThreadId ?? undefined),
    enabled: enabled && canRead && conversationId !== null,
    staleTime: 30_000,
    // 403 = "sem permissão" determinístico → nunca re-tentar (não é transitório).
    // Demais erros: até 1 re-tentativa (rede transitória), depois falham honesto.
    retry: (failureCount, error) => !isForbidden(error) && failureCount < 1,
    retryDelay: 200,
  });

  const denied = isForbidden(query.error);
  const deniedDetail = denied ? ((query.error as ApiError).body as ContextPeekDenied) : null;

  return {
    data: query.data,
    denied,
    deniedDetail,
    isLoading: query.isLoading,
    // Um 403 NÃO é `isError` para o consumidor — é um estado de produto (`denied`).
    isError: query.isError && !denied,
    refetch: () => void query.refetch(),
  };
}
